import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Building2,
  Filter,
  PanelRight,
  PanelRightClose,
  Send,
  Sparkles,
  Users,
} from 'lucide-react';
import { useProspecting } from '../../context/ProspectingContext';
import { getSuggestions } from './aiResponder';
import {
  sendProspectingAssistantMessage,
  searchTypeToEntityType,
} from './prospectingApi';
import {
  mapUiFiltersToCanonical,
  resolveAssistantFiltersFromResponse,
} from './prospectingAssistantFilters';

const ASSISTANT_MARKDOWN_PLUGINS = [remarkGfm];

const ASSISTANT_MARKDOWN_COMPONENTS = {
  p({ children }) {
    return <p className="my-1.5 first:mt-0 last:mb-0 whitespace-pre-wrap">{children}</p>;
  },
  ul({ children }) {
    return <ul className="my-2 ml-1 list-disc list-outside space-y-1 pl-4 marker:text-gray-400 dark:marker:text-gray-500">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="my-2 ml-1 list-decimal list-outside space-y-1 pl-4 marker:text-gray-400 dark:marker:text-gray-500">{children}</ol>;
  },
  li({ children }) {
    return <li className="pl-0.5">{children}</li>;
  },
  strong({ children }) {
    return <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>;
  },
  code({ children, ...props }) {
    const hasNewlines = String(children).includes('\n');
    const codeProps = markdownElementProps(props);

    if (hasNewlines) {
      return (
        <code
          className="my-2 block overflow-x-auto rounded-lg border border-gray-200/70 bg-white/70 px-2.5 py-2 text-xs leading-relaxed text-gray-800 dark:border-gray-700/70 dark:bg-gray-900/60 dark:text-gray-100"
          {...codeProps}
        >
          {children}
        </code>
      );
    }

    return (
      <code
        className="rounded-md border border-gray-200/70 bg-white/70 px-1 py-0.5 text-[0.8em] text-gray-800 dark:border-gray-700/70 dark:bg-gray-900/60 dark:text-gray-100"
        {...codeProps}
      >
        {children}
      </code>
    );
  },
  a({ children, href, ...props }) {
    const linkProps = markdownElementProps(props);

    return (
      <a
        {...linkProps}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 transition-colors hover:text-brand-800 hover:decoration-brand-500 dark:text-brand-300 dark:decoration-brand-700 dark:hover:text-brand-200"
      >
        {children}
      </a>
    );
  },
};

function markdownElementProps(props) {
  const elementProps = { ...props };
  delete elementProps.node;
  return elementProps;
}

function AssistantMarkdown({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={ASSISTANT_MARKDOWN_PLUGINS}
      components={ASSISTANT_MARKDOWN_COMPONENTS}
      skipHtml
    >
      {content}
    </ReactMarkdown>
  );
}

function AssistantHeader({ onToggle }) {
  return (
    <div className="h-12 flex items-center justify-between px-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
        <Sparkles className="w-4 h-4 text-brand-500" /> Able
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gray-400">AI prospecting assistant</span>
        <button
          type="button"
          onClick={onToggle}
          title="Collapse Able"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <PanelRightClose className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function CollapsedAssistantRail({ onToggle, hasMessages, thinking }) {
  return (
    <aside className="w-12 flex-shrink-0 border-l border-gray-100 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/50 flex flex-col items-center">
      <div className="h-12 flex w-full items-center justify-center border-b border-gray-100 dark:border-gray-700">
        <button
          type="button"
          onClick={onToggle}
          title="Expand Able"
          className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <PanelRight className="h-4 w-4" />
          {(hasMessages || thinking) && (
            <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand-500" />
          )}
        </button>
      </div>
      <button
        type="button"
        onClick={onToggle}
        title="Able"
        className="mt-3 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600 transition-colors hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50"
      >
        <Sparkles className="h-4 w-4" />
      </button>
      {thinking && (
        <div className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-500 animate-pulse" />
      )}
    </aside>
  );
}

function UserMessage({ content }) {
  return (
    <div className="flex justify-end animate-fadeIn">
      <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-brand-600 text-white px-3.5 py-2 text-sm leading-relaxed">
        {content}
      </div>
    </div>
  );
}

function AssistantMessage({ content, toolCalls }) {
  return (
    <div className="flex justify-start animate-fadeIn">
      <div className="max-w-[88%]">
        <div className="rounded-2xl rounded-tl-md bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 px-3.5 py-2 text-sm leading-relaxed">
          <AssistantMarkdown content={content} />
        </div>
        {toolCalls && toolCalls.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {toolCalls.map((tc, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-md bg-brand-50 dark:bg-brand-900/30 border border-brand-100 dark:border-brand-800 px-1.5 py-0.5 text-[10px] font-medium text-brand-700 dark:text-brand-300"
              >
                <Filter className="w-2.5 h-2.5" /> Applied: {tc.args?.label || tc.args?.field}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="flex justify-start animate-fadeIn">
      <div className="rounded-2xl rounded-tl-md bg-gray-100 dark:bg-gray-800 px-3.5 py-2.5 inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: '120ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: '240ms' }} />
      </div>
    </div>
  );
}

function EmptyConversation({ searchType }) {
  return (
    <div className="px-4 py-6 text-center">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-900/30 mb-2">
        <Sparkles className="w-4 h-4 text-brand-500" />
      </div>
      <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
        What kind of {searchType === 'companies' ? 'companies' : 'people'} are you looking for?
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Describe them in your own words. I’ll narrow the list down on the left.
      </p>
    </div>
  );
}

function SuggestionChip({ text, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(text)}
      className="w-full text-left rounded-xl border border-gray-150 dark:border-gray-700/80 bg-white dark:bg-gray-800/50 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 transition-all hover:border-brand-300 dark:hover:border-brand-700 hover:text-brand-700 dark:hover:text-brand-300 hover:shadow-sm"
    >
      {text}
    </button>
  );
}

function TypeToggle({ value, onChange }) {
  const peopleComingSoonTooltip = 'People prospecting is coming soon';
  const buttonBase = 'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors';
  return (
    <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 w-full">
      <button
        type="button"
        onClick={(event) => event.preventDefault()}
        aria-disabled="true"
        title={peopleComingSoonTooltip}
        aria-label={peopleComingSoonTooltip}
        className={`${buttonBase} ${
          value === 'people'
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-400 dark:text-gray-500'
        } cursor-not-allowed opacity-75`}
      >
        <Users className="w-3 h-3" />
        <span className="whitespace-nowrap">People (coming soon)</span>
      </button>
      <button
        type="button"
        onClick={() => onChange('companies')}
        className={`${buttonBase} ${
          value === 'companies'
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        <Building2 className="w-3 h-3" /> Companies
      </button>
    </div>
  );
}

function ChatComposer({ value, onChange, onSubmit, disabled }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div className="relative rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus-within:border-brand-400 dark:focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-100 dark:focus-within:ring-brand-900/40 transition-all">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={3}
        placeholder="Describe who you’re looking for…"
        disabled={disabled}
        className="w-full resize-none bg-transparent px-3 py-2.5 pr-10 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none disabled:opacity-60"
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSend}
        className={`absolute right-1.5 bottom-1.5 inline-flex items-center justify-center w-7 h-7 rounded-lg transition-all ${
          canSend
            ? 'bg-brand-600 hover:bg-brand-700 text-white'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-300 dark:text-gray-500 cursor-not-allowed'
        }`}
        aria-label="Send"
      >
        <Send className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function assistantReplyFromResponse(response) {
  const candidates = [
    response?.assistant_message,
    response?.message,
    response?.reply,
    response?.assistant?.message,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return 'I updated your filters based on your request.';
}

function assistantErrorMessage(error) {
  const detail = typeof error?.message === 'string' ? error.message.trim() : '';
  if (detail) return `I ran into an issue: ${detail}`;
  return 'I could not update your filters right now, but your current setup is still intact.';
}

const MAX_ASSISTANT_CONVERSATION_MESSAGES = 19;

function normalizeConversationMessage(message) {
  if (!message || (message.role !== 'user' && message.role !== 'assistant')) {
    return null;
  }

  const content = String(message.content || '').trim();
  if (!content) return null;

  return {
    role: message.role,
    content,
  };
}

export default function ProspectingAssistantPanel({ search, collapsed = false, onToggle }) {
  const {
    addChatMessage,
    applyFilter,
    clearFilters,
    setSearchType,
  } = useProspecting();
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const messagesEndRef = useRef(null);

  const suggestions = getSuggestions(search);
  const hasMessages = search.chatMessages.length > 0;

  // Auto-scroll on new message / thinking change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [search.chatMessages.length, thinking]);

  const handleSend = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;
    setDraft('');

    const conversation = (search.chatMessages || [])
      .map(normalizeConversationMessage)
      .filter(Boolean)
      .slice(-MAX_ASSISTANT_CONVERSATION_MESSAGES);

    addChatMessage(search.id, { role: 'user', content: trimmed });
    setThinking(true);

    try {
      const response = await sendProspectingAssistantMessage({
        entityType: searchTypeToEntityType(search.type),
        userMessage: trimmed,
        currentFilters: mapUiFiltersToCanonical(search.type, search.filters),
        conversation,
      });

      const reply = assistantReplyFromResponse(response);
      const { nextFilters, toolCalls } = resolveAssistantFiltersFromResponse({
        response,
        searchType: search.type,
      });

      addChatMessage(search.id, { role: 'assistant', content: reply, toolCalls });

      if (Array.isArray(nextFilters)) {
        clearFilters(search.id);
        nextFilters.forEach((filter) => {
          applyFilter(search.id, filter);
        });
      }
    } catch (error) {
      addChatMessage(search.id, {
        role: 'assistant',
        content: assistantErrorMessage(error),
      });
    } finally {
      setThinking(false);
    }
  }, [thinking, addChatMessage, applyFilter, clearFilters, search]);

  const handleSuggestion = (text) => {
    setDraft(text);
    // small UX nicety: fire immediately on click
    handleSend(text);
  };

  const handleSubmit = () => handleSend(draft);

  if (collapsed) {
    return (
      <CollapsedAssistantRail
        onToggle={onToggle}
        hasMessages={hasMessages}
        thinking={thinking}
      />
    );
  }

  return (
    <aside className="w-[420px] flex-shrink-0 border-l border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex flex-col">
      <AssistantHeader onToggle={onToggle} />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
        {!hasMessages && <EmptyConversation searchType={search.type} />}
        {search.chatMessages.map((m) => (
          m.role === 'user'
            ? <UserMessage key={m.id} content={m.content} />
            : <AssistantMessage key={m.id} content={m.content} toolCalls={m.toolCalls} />
        ))}
        {thinking && <ThinkingBubble />}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      <div className="px-4 pt-3 pb-2 border-t border-gray-100 dark:border-gray-700 bg-white/50 dark:bg-gray-900/50 flex-shrink-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
          Suggestions
        </div>
        <div className="space-y-1.5">
          {suggestions.map((s) => (
            <SuggestionChip key={s} text={s} onClick={handleSuggestion} />
          ))}
        </div>
      </div>

      {/* Type toggle */}
      <div className="px-4 pt-2 pb-2 flex-shrink-0">
        <TypeToggle
          value={search.type}
          onChange={(t) => setSearchType(search.id, t)}
        />
      </div>

      {/* Composer */}
      <div className="px-4 pb-4 flex-shrink-0">
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={handleSubmit}
          disabled={thinking}
        />
      </div>
    </aside>
  );
}
